import {Button, ButtonGroup, closeCurrentDialog, showNotification} from 'platform/components';
import {Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {ApiError} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {businessCaseRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {composePath, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {useInterestToBusinessCase} from '../hooks/useInterestToBusinessCase';

interface CloneVehiclesConfirmDialogProps extends RequiredTestIdProps {
  interestId: string;
  clonedVehicleMapRef: {current: Map<string, string>};
}

export function CloneVehiclesConfirmDialog(props: CloneVehiclesConfirmDialogProps) {
  const navigate = useNavigate();
  const [activeAction, setActiveAction] = useState<'clone' | 'convert' | null>(null);

  const {cloneAndConvert, convertOnly, isMandatoryFieldsError, handleMandatoryFieldsError} =
    useInterestToBusinessCase({
      interestId: props.interestId,
      clonedVehicleMapRef: props.clonedVehicleMapRef,
    });

  const onSuccess = (businessCaseId: string) => {
    showNotification.success();
    navigate(composePath(businessCaseRoutes.overview, {params: {id: businessCaseId}}));
    closeCurrentDialog();
  };

  const onError = (error: ApiError) => {
    if (isMandatoryFieldsError(error)) {
      handleMandatoryFieldsError(error.data?.validationErrors ?? []);
      setActiveAction(null);
      return;
    }

    handleApiError(error);
    setActiveAction(null);
  };

  const handleWithClone = () => {
    setActiveAction('clone');

    cloneAndConvert()
      .then((res) => onSuccess(res.businessCaseId))
      .catch(onError);
  };

  const handleWithoutClone = () => {
    setActiveAction('convert');

    convertOnly()
      .then((res) => onSuccess(res.businessCaseId))
      .catch(onError);
  };

  return (
    <VStack spacing={4}>
      <Text>{i18n.t('entity.interest.labels.cloneVehiclesQuestion')}</Text>
      <ButtonGroup align="right">
        <Button
          variant="secondary"
          title={i18n.t('entity.interest.actions.createBusinessCaseWithoutClone')}
          isDisabled={activeAction !== null}
          isLoading={activeAction === 'convert'}
          onClick={handleWithoutClone}
          data-testid={suffixTestId('no', props)}
        />
        <Button
          variant="primary"
          title={i18n.t('entity.interest.actions.cloneAndCreateBusinessCase')}
          isDisabled={activeAction !== null}
          isLoading={activeAction === 'clone'}
          onClick={handleWithClone}
          data-testid={suffixTestId('yes', props)}
        />
      </ButtonGroup>
    </VStack>
  );
}
